import React, { useRef, useState, useCallback } from "react";
import {
    intl,
    DmsAltBox,
    DmsTabber,
    DmsTable,
    DmsTableDeleteButton,
    DmsButton,
    DmsButtonGroup,
    DmsFlexContainer,
    DmsSelect,
    DmsTextField,
    DmsSwitch,
    fetchData,
    HTTP_STATUS,
    useGet
} from "dmsbasereact";
import {
    Typography,
    Grid,
    Modal,
    Box
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import Swal from "sweetalert2";

import { SettingsViewEdit } from "../../components/SettingsLayouts";
import { Permissions } from "../../constants/Permissions";

const INSTITUTION_CATEGORIES = [
    { value: 100, label: "Distributor" },
    { value: 101, label: "Dispenser" },
    { value: 102, label: "Intermediary" },
    { value: 103, label: "Prescriber" },
    { value: 104, label: "Buyer" }
];

const getCategoryLabel = (categoryValue) => {
    const category = INSTITUTION_CATEGORIES.find(c => c.value === categoryValue);
    if (!category) return categoryValue;
    return intl.get(category.label).d(category.label);
};

const getCategoryOptions = () => {
    return INSTITUTION_CATEGORIES.map(cat => ({
        value: cat.value,
        label: intl.get(cat.label).d(cat.label)
    }));
};

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
};

const DocumentRequirementsTab = () => {
    const tableRef = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [formData, setFormData] = useState({
        institution_category: 100,
        document_name: "",
        is_required: true,
        requires_expiration: false,
        sort_order: 0
    });

    const resetForm = useCallback(() => {
        setFormData({
            institution_category: 100,
            document_name: "",
            is_required: true,
            requires_expiration: false,
            sort_order: 0
        });
        setSelectedItem(null);
        setEditMode(false);
    }, []);

    const handleOpenCreate = useCallback(() => {
        resetForm();
        setModalOpen(true);
    }, [resetForm]);

    const handleOpenEdit = useCallback((row) => {
        setFormData({
            institution_category: row.institution_category,
            document_name: row.document_name,
            is_required: row.is_required ?? true,
            requires_expiration: row.requires_expiration ?? false,
            sort_order: row.sort_order ?? 0
        });
        setSelectedItem(row);
        setEditMode(true);
        setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleFieldChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!formData.document_name?.trim()) {
            Swal.fire({
                icon: "warning",
                title: intl.get("DocumentNameRequired").d("Document name is required")
            });
            return;
        }

        try {
            const payload = {
                institution_category: formData.institution_category,
                document_name: formData.document_name.trim(),
                is_required: formData.is_required,
                requires_expiration: formData.requires_expiration,
                sort_order: formData.sort_order === "" ? 0 : formData.sort_order
            };

            let response;
            if (editMode && selectedItem) {
                response = await fetchData(
                    `/document_requirement/${selectedItem.pk}`,
                    "PUT",
                    true,
                    payload,
                    false,
                    true
                );
            } else {
                response = await fetchData(
                    "/document_requirement",
                    "POST",
                    true,
                    payload,
                    false,
                    true
                );
            }

            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                Swal.fire({
                    icon: "success",
                    title: editMode
                        ? intl.get("DocumentRequirementUpdated").d("Document requirement updated successfully")
                        : intl.get("DocumentRequirementCreated").d("Document requirement created successfully")
                });
                tableRef.current?.updateTable();
                handleCloseModal();
            } else {
                Swal.fire({
                    icon: "error",
                    title: response?.data?.detail || intl.get("OperationFailed").d("Operation failed")
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: intl.get("OperationFailed").d("Operation failed"),
                text: error?.response?.data?.detail || error.message
            });
        }
    }, [formData, editMode, selectedItem, handleCloseModal]);

    const header = [
        {
            label: intl.get("InstitutionCategory").d("Institution Category"),
            column_name: "institution_category",
            filter: true,
            control: "select",
            options: getCategoryOptions(),
            element: (row) => getCategoryLabel(row.institution_category)
        },
        {
            label: intl.get("DocumentName").d("Document Name"),
            column_name: "document_name",
            filter: true
        },
        {
            label: intl.get("IsRequired").d("Is Required"),
            column_name: "is_required",
            width: "120px",
            element: (row) => row.is_required
                ? intl.get("Yes").d("Yes")
                : intl.get("No").d("No")
        },
        {
            label: intl.get("RequiresExpiration").d("Requires Expiration"),
            column_name: "requires_expiration",
            width: "150px",
            element: (row) => row.requires_expiration
                ? intl.get("Yes").d("Yes")
                : intl.get("No").d("No")
        },
        {
            label: intl.get("SortOrder").d("Sort Order"),
            column_name: "sort_order",
            width: "100px"
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            width: "120px",
            sortable: false,
            element: (row) => (
                <DmsButtonGroup>
                    <DmsButton
                        icon={<EditIcon />}
                        title={intl.get("Edit").d("Edit")}
                        onClick={() => handleOpenEdit(row)}
                    />
                    <DmsTableDeleteButton
                        table={tableRef}
                        title={intl.get("Delete").d("Delete")}
                        text={intl.get("ConfirmDeleteDocumentRequirement").d("Are you sure you want to delete this document requirement?")}
                        url="/document_requirement/"
                        row_pk={row.pk}
                    />
                </DmsButtonGroup>
            )
        }
    ];

    const toolbar = (
        <DmsFlexContainer>
            <DmsFlexContainer.Left>
                <DmsButton
                    variant="primary"
                    icon={<AddIcon />}
                    text={intl.get("AddRequirement").d("Add Requirement")}
                    onClick={handleOpenCreate}
                />
            </DmsFlexContainer.Left>
        </DmsFlexContainer>
    );

    return (
        <>
            <DmsTable
                header={header}
                managed={true}
                needs_authentication={true}
                export_table={false}
                ref={tableRef}
                url="/document_requirement/table"
                toolbar={toolbar}
            />

            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="document-requirement-modal-title"
            >
                <Box sx={modalStyle}>
                    <Typography id="document-requirement-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        {editMode
                            ? intl.get("EditDocumentRequirement").d("Edit Document Requirement")
                            : intl.get("AddDocumentRequirement").d("Add Document Requirement")
                        }
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <DmsSelect
                                name="institution_category"
                                label={intl.get("InstitutionCategory").d("Institution Category")}
                                fullWidth
                                value={formData.institution_category}
                                onChange={(value) => handleFieldChange("institution_category", value)}
                                options={getCategoryOptions()}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DmsTextField
                                name="document_name"
                                label={intl.get("DocumentName").d("Document Name")}
                                fullWidth
                                value={formData.document_name}
                                onChange={(e) => handleFieldChange("document_name", e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsSwitch
                                name="is_required"
                                label={intl.get("IsRequired").d("Is Required")}
                                checked={formData.is_required}
                                onChange={(name, value) => handleFieldChange("is_required", value === "true" || value === true)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsSwitch
                                name="requires_expiration"
                                label={intl.get("RequiresExpiration").d("Requires Expiration")}
                                checked={formData.requires_expiration}
                                onChange={(name, value) => handleFieldChange("requires_expiration", value === "true" || value === true)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DmsTextField
                                name="sort_order"
                                label={intl.get("SortOrder").d("Sort Order")}
                                fullWidth
                                type="number"
                                value={formData.sort_order}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    handleFieldChange("sort_order", val === "" ? "" : parseInt(val));
                                }}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <DmsButton
                            variant="secondary"
                            text={intl.get("Cancel").d("Cancel")}
                            onClick={handleCloseModal}
                        />
                        <DmsButton
                            variant="primary"
                            text={editMode ? intl.get("Update").d("Update") : intl.get("Create").d("Create")}
                            onClick={handleSubmit}
                        />
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

const ExpirationSettingsViewContent = ({ data }) => {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                    {intl.get("FirstAlertDays").d("First Alert (days)")}
                </Typography>
                <Typography variant="body1">
                    {data.alert_days_first ?? 30}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                    {intl.get("SecondAlertDays").d("Second Alert (days)")}
                </Typography>
                <Typography variant="body1">
                    {data.alert_days_second ?? 7}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                    {intl.get("FinalAlertDays").d("Final Alert (days)")}
                </Typography>
                <Typography variant="body1">
                    {data.alert_days_final ?? 1}
                </Typography>
            </Grid>
        </Grid>
    );
};

const ExpirationSettingsEditContent = ({ formData, onFieldChange }) => {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
                <DmsTextField
                    name="alert_days_first"
                    label={intl.get("FirstAlertDays").d("First Alert (days)")}
                    fullWidth
                    type="number"
                    value={formData.alert_days_first ?? 30}
                    onChange={(e) => {
                        const val = e.target.value;
                        onFieldChange("alert_days_first", val === "" ? "" : parseInt(val));
                    }}
                    inputProps={{ min: 1, max: 365 }}
                    helperText={intl.get("AlertDaysFirstHelp").d("Days before expiration for first alert")}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <DmsTextField
                    name="alert_days_second"
                    label={intl.get("SecondAlertDays").d("Second Alert (days)")}
                    fullWidth
                    type="number"
                    value={formData.alert_days_second ?? 7}
                    onChange={(e) => {
                        const val = e.target.value;
                        onFieldChange("alert_days_second", val === "" ? "" : parseInt(val));
                    }}
                    inputProps={{ min: 1, max: 365 }}
                    helperText={intl.get("AlertDaysSecondHelp").d("Days before expiration for second alert")}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <DmsTextField
                    name="alert_days_final"
                    label={intl.get("FinalAlertDays").d("Final Alert (days)")}
                    fullWidth
                    type="number"
                    value={formData.alert_days_final ?? 1}
                    onChange={(e) => {
                        const val = e.target.value;
                        onFieldChange("alert_days_final", val === "" ? "" : parseInt(val));
                    }}
                    inputProps={{ min: 1, max: 365 }}
                    helperText={intl.get("AlertDaysFinalHelp").d("Days before expiration for final alert")}
                />
            </Grid>
        </Grid>
    );
};

const ExpirationSettingsTab = () => {
    const { data, isLoading, refetch } = useGet("/document_expiration_settings", null, true);

    const handleSave = async (payload) => {
        try {
            const response = await fetchData(
                "/document_expiration_settings",
                "PUT",
                true,
                payload,
                false,
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                return response?.data?.data || response?.data;
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    return (
        <SettingsViewEdit
            data={data?.data}
            loading={isLoading}
            title={intl.get("ExpirationAlertSettings").d("Expiration Alert Settings")}
            description={intl.get("ExpirationAlertSettingsDescription").d(
                "Configure when alerts should be sent before document expiration."
            )}
            viewContent={ExpirationSettingsViewContent}
            editContent={ExpirationSettingsEditContent}
            prepareFormData={(data) => ({
                alert_days_first: data.alert_days_first ?? 30,
                alert_days_second: data.alert_days_second ?? 7,
                alert_days_final: data.alert_days_final ?? 1
            })}
            buildPayload={(formData) => ({
                alert_days_first: formData.alert_days_first === "" ? 30 : formData.alert_days_first,
                alert_days_second: formData.alert_days_second === "" ? 7 : formData.alert_days_second,
                alert_days_final: formData.alert_days_final === "" ? 1 : formData.alert_days_final
            })}
            onSave={handleSave}
            onDataUpdated={refetch}
        />
    );
};

export const DocumentSettings = () => {
    const tabs = [
        {
            title: intl.get("DocumentRequirements").d("Document Requirements"),
            component: <DocumentRequirementsTab />,
            permissions: [Permissions.CAN_MANAGE_GENERAL_SETTINGS]
        },
        {
            title: intl.get("ExpirationSettings").d("Expiration Settings"),
            component: <ExpirationSettingsTab />,
            permissions: [Permissions.CAN_MANAGE_GENERAL_SETTINGS]
        }
    ];

    return (
        <DmsAltBox.Card>
            <DmsAltBox.Header>
                <Typography variant="h6">
                    {intl.get("DocumentSettings").d("Document Settings")}
                </Typography>
            </DmsAltBox.Header>
            <DmsAltBox.Body>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {intl.get("DocumentSettingsDescription").d(
                        "Manage document requirements for institution registration and configure expiration alert thresholds."
                    )}
                </Typography>
                <DmsTabber tabs={tabs} pageName={intl.get("DocumentSettings").d("Document Settings")} />
            </DmsAltBox.Body>
        </DmsAltBox.Card>
    );
};
